import { ChangeEvent, FormEvent, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { authApi } from '@services/authApi'
import './PortalLoginPage.css'

/**
 * Reset-password page (KAN-782). Reads the single-use token from the `?token=` query param (the
 * emailed link), collects + confirms a new password, and posts to `POST /api/v2/auth/password/reset`.
 * On success the backend has set the password and revoked existing sessions, so we send the user to
 * sign in fresh.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [isSubmitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)

    if (!token) {
      setError('This reset link is invalid or has expired. Please request a new one.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      await authApi.resetPassword(token, password)
      setDone(true)
    } catch (err) {
      // The backend rejects unknown/expired/used tokens uniformly; surface a single generic message.
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'This reset link is invalid or has expired. Please request a new one.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const onPassword = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value)
    setError(null)
  }
  const onConfirm = (e: ChangeEvent<HTMLInputElement>) => {
    setConfirm(e.target.value)
    setError(null)
  }

  return (
    <div className="portal-login portal-login-customer">
      <div className="portal-login__layout">
        <div className="portal-login__card">
          <header className="portal-login__header">
            <span className="portal-login__badge">RESET PASSWORD</span>
            <h1>Choose a new password</h1>
            <div className="portal-login__description">
              <p>Enter a new password for your account.</p>
            </div>
          </header>

          {done ? (
            <div className="portal-login__form">
              <p className="portal-login__otp-hint">
                Your password has been reset. Please sign in with your new password.
              </p>
              <button
                type="button"
                className="portal-login__submit"
                onClick={() => navigate('/login')}
              >
                Go to Sign In
              </button>
            </div>
          ) : (
            <form className="portal-login__form" onSubmit={handleSubmit}>
              <label className="portal-login__form-label" htmlFor="reset-password">
                New Password
              </label>
              <input
                id="reset-password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={onPassword}
                disabled={isSubmitting}
              />

              <label className="portal-login__form-label" htmlFor="reset-confirm">
                Confirm Password
              </label>
              <input
                id="reset-confirm"
                name="confirm"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={confirm}
                onChange={onConfirm}
                disabled={isSubmitting}
              />

              {error && <div className="portal-login__error">{error}</div>}

              <button type="submit" className="portal-login__submit" disabled={isSubmitting}>
                {isSubmitting ? 'Resetting…' : 'Reset Password'}
              </button>
            </form>
          )}

          <footer className="portal-login__footer">
            <button
              type="button"
              className="portal-login__secondary"
              onClick={() => navigate('/login')}
              disabled={isSubmitting}
            >
              Back to Sign In
            </button>
          </footer>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
